import { apiHandler } from "api";
import React, { createContext, useContext, useState, useEffect } from "react";
import { endpoint } from "../../api/endpoint";
import {
  uniqueNamesGenerator,
  adjectives,
  animals,
} from "unique-names-generator";
const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      "useNavigation must be used within a NavigationStateManager"
    );
  }
  return context;
};

const NavigationStateManager = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [loader, setLoader] = useState(false);
  const [currentStore, setCurrentStore] = useState(
    sessionStorage.getItem("selectedStore")
      ? JSON.parse(sessionStorage.getItem("selectedStore"))
      : null
  );
  const [anonymousMode, setAnonymousMode] = useState(true);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [workflowStep, setWorkflowStep] = useState(0);

  useEffect(() => {
    const savedStore = sessionStorage.getItem("selectedStore");
    const savedAnonymousMode =
      sessionStorage.getItem("anonymousMode") === "true";
    const isAnonymousModeKeyPresent =
      sessionStorage.getItem("anonymousMode") !== null;
    const savedHistory = sessionStorage.getItem("navigationHistory");
    const savedWorkflowStep = sessionStorage.getItem("workflowStep");

    if (savedStore) {
      try {
        setCurrentStore(JSON.parse(savedStore));
      } catch (error) {
        console.error("Error parsing saved store:", error);
        sessionStorage.removeItem("selectedStore");
      }
    }
    if (isAnonymousModeKeyPresent) {
      setAnonymousMode(savedAnonymousMode);
    } else {
      setAnonymousMode(true);
    }

    if (savedHistory) {
      try {
        setNavigationHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error("Error parsing navigation history:", error);
        sessionStorage.removeItem("navigationHistory");
      }
    }

    if (savedWorkflowStep) {
      setWorkflowStep(parseInt(savedWorkflowStep, 10) || 0);
    }
  }, []);

  const handleStoreSelection = (store) => {
    setCurrentStore(store);
    sessionStorage.setItem("selectedStore", JSON.stringify(store));

    const newStep = 1;
    setWorkflowStep(newStep);
    sessionStorage.setItem("workflowStep", newStep.toString());

    const historyEntry = {
      timestamp: Date.now(),
      action: "store_selected",
      store: store,
      anonymousMode: anonymousMode,
    };

    const updatedHistory = [...navigationHistory, historyEntry];
    setNavigationHistory(updatedHistory);
    sessionStorage.setItem("navigationHistory", JSON.stringify(updatedHistory));
  };

  const handleAnonymousModeToggle = () => {
    const newMode = !anonymousMode;
    setAnonymousMode(newMode);
    sessionStorage.setItem("anonymousMode", newMode.toString());

    const historyEntry = {
      timestamp: Date.now(),
      action: "anonymous_mode_toggled",
      anonymousMode: newMode,
      store: currentStore,
    };

    const updatedHistory = [...navigationHistory, historyEntry];
    setNavigationHistory(updatedHistory);
    sessionStorage.setItem("navigationHistory", JSON.stringify(updatedHistory));
  };

  const handleNavigationStep = (step, additionalData = {}) => {
    setWorkflowStep(step);
    sessionStorage.setItem("workflowStep", step.toString());

    const historyEntry = {
      timestamp: Date.now(),
      action: "navigation_step",
      step: step,
      store: currentStore,
      anonymousMode: anonymousMode,
      ...additionalData,
    };

    const updatedHistory = [...navigationHistory, historyEntry];
    setNavigationHistory(updatedHistory);
    sessionStorage.setItem("navigationHistory", JSON.stringify(updatedHistory));
  };

  const clearNavigationState = () => {
    setCurrentStore(null);
    setAnonymousMode(false);
    setNavigationHistory([]);
    setWorkflowStep(0);

    sessionStorage.removeItem("selectedStore");
    sessionStorage.removeItem("anonymousMode");
    sessionStorage.removeItem("navigationHistory");
    sessionStorage.removeItem("workflowStep");
  };
  const getDisplayStoreName = (store = currentStore) => {
    if (!store) return null;

    if (anonymousMode) {
      return uniqueNamesGenerator({
        dictionaries: [adjectives, animals],
        seed: store.iTenantId,
        separator: " ",
        style: "capital",
      });
    }

    return store.sBrandName;
  };

  const getDisplayProductName = (product) => {
    if (!product) return null;

    if (anonymousMode) {
      return uniqueNamesGenerator({
        dictionaries: [adjectives, animals],
        seed: product.iProductId,
        separator: " ",
        style: "capital",
      });
    }

    return product.sProductName;
  };

  const canProceedToStep = (step) => {
    switch (step) {
      case 1:
        return currentStore !== null;
      case 2:
        return currentStore !== null && workflowStep >= 1;
      default:
        return true;
    }
  };

  const getWorkflowProgress = () => {
    const totalSteps = 3;
    return Math.round((workflowStep / totalSteps) * 100);
  };

  const getStores = async () => {
    try {
      setLoader(true);
      const response = await apiHandler({
        url: `${endpoint.GET_STORES}`,
        method: "GET",
      });

      if (response.data.data?.tenants) {
        setStores(response.data.data?.tenants);
      }
    } catch (error) {
      console.log("Error fetching tenant details", error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    getStores();
  }, []);

  const getAnonymousName = (id) => {
    return uniqueNamesGenerator({
      dictionaries: [adjectives, animals],
      seed: id,
      separator: " ",
      style: "capital",
    });
  };

  const contextValue = {
    currentStore,
    anonymousMode,
    navigationHistory,
    workflowStep,
    handleStoreSelection,
    handleAnonymousModeToggle,
    handleNavigationStep,
    clearNavigationState,
    getDisplayStoreName,
    canProceedToStep,
    getWorkflowProgress,
    loader,
    stores,
    getAnonymousName,
    getDisplayProductName,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationStateManager;
